import { useInfiniteQuery, useQuery, useQueryClient } from "react-query";
import {
  getOrderDetails,
  getOrderList,
  getReturnsDetails,
  getRiderOrderDetails,
  getRiderOrders,
} from "../../../services/inventory";
import { useInView } from "react-intersection-observer";
import { cleanObject, reArrangeCartItems } from "../../../helpers";
import { useEffect } from "react";
import {
  getCartItems,
  getPendingSessions,
  getPharmacyOption,
} from "../../../services/sales-point";

export const useGetOrderDetails = ({ id, isEnabled = true }) => {
  return useQuery({
    queryKey: ["orderDetails", `${id}`],
    queryFn: () => getOrderDetails(id).then((res) => res?.data?.data),
    enabled: isEnabled && id ? true : false,
    staleTime: Infinity,
  });
};

export const useGetRiderOrders = ({ queries = "" }) => {
  const { ref: autoScrollRef, inView } = useInView();
  const riderOrdersQueries = useInfiniteQuery(
    ["riderOrders", queries],
    ({ pageParam }) =>
      getRiderOrders(
        cleanObject({
          page: pageParam,
          queries,
        }),
      ).then((res) => res?.data),
    {
      enabled: queries !== "" ? true : false,
      staleTime: Infinity,
      getNextPageParam: ({ meta: { next_page } }) =>
        next_page === null ? undefined : next_page,
    },
  );
  const { data, hasNextPage, fetchNextPage } = riderOrdersQueries;
  const riderOrders = new Map();
  // eslint-disable-next-line array-callback-return
  data?.pages?.map((page) => {
    riderOrders.set(`${page.meta.current_page}`, page?.data);
  });
  const riderOrdersList = [].concat(...riderOrders.values());

  useEffect(() => {
    if (inView && hasNextPage) fetchNextPage();
  }, [inView, fetchNextPage, hasNextPage]);

  return {
    ...riderOrdersQueries,
    autoScrollRef,
    data: riderOrdersList,
  };
};

export const useGetStorefrontOrderDetails = ({ id }) => {
  return useQuery(
    ["storefrontOrderDetails", id],
    () => getRiderOrderDetails(id).then((res) => res?.data?.data),
    { staleTime: Infinity },
  );
};

export const useGetOrderList = ({
  queries = "",
  queryParams = {},
  isEnabled = true,
}) => {
  return useQuery(
    ["getOrderList", queries, queryParams],
    () => getOrderList(queries, queryParams).then((res) => res?.data),
    { staleTime: Infinity, enabled: isEnabled },
  );
};

export const useGetPendingSessions = ({ queries = "", isEnabled = true }) => {
  return useQuery(
    ["getPendingSessions", queries],
    () =>
      getPendingSessions(queries).then((res) => {
        return res?.data;
      }),
    {
      staleTime: Infinity,
      enabled: isEnabled,
    },
  );
};

export const useGetCartItems = ({ store_id, isEnabled = false }) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isEnabled) {
      queryClient.removeQueries(["getCartItems", store_id]);
    }
  }, [isEnabled, store_id, queryClient]);

  const query = useQuery(
    ["getCartItems", store_id],
    () =>
      getCartItems(store_id).then((res) => {
        const newData = reArrangeCartItems([...res?.data?.data], store_id);
        return { cartItems: newData };
      }),
    {
      enabled: isEnabled,
      cacheTime: 0,
      keepPreviousData: false,
    },
  );

  return isEnabled
    ? query
    : { data: undefined, isLoading: false, isError: false };
};

export const useGetReturnsDetails = ({ id, isEnabled = true }) => {
  return useQuery({
    queryKey: ["returnsDetails", `${id}`],
    queryFn: () => getReturnsDetails(id).then((res) => res?.data?.data),
    enabled: isEnabled && id ? true : false,
    staleTime: Infinity,
  });
};

export const useGetPharmacyOption = ({ isEnabled = true }) => {
  return useQuery(
    ["getPharmacyOption"],
    () =>
      getPharmacyOption().then((res) => {
        return res?.data?.data;
      }),
    {
      staleTime: Infinity,
      enabled: isEnabled,
    },
  );
};
